/**
 * Install on Android: the APK or the PWA, honestly labelled.
 *
 * The APK is built by `.github/workflows/android.yml`; `/api/downloads/apk`
 * either streams a file the deployment keeps outside its own directory
 * (`APK_FILE_PATH`) or redirects at a URL that serves one (`APK_DOWNLOAD_URL`).
 * Neither set, and the button still renders, but it names the missing piece
 * instead of downloading a 404. The PWA path needs no build at all.
 *
 * Nothing of it is shown inside the shell. A person already using the APK is not
 * being offered the APK, and the two instructions - sideload this, or add the site
 * to your home screen - are both about getting to where they already are.
 */

import { useState } from 'react';
import { capacitorBridge, type CapacitorGlobals } from '../ota/host.js';

const APK_PATH = '/api/downloads/apk';

export interface InstallCardProps {
  /** Injected in tests: the Capacitor global, which is how the shell is known. */
  capacitor?: CapacitorGlobals;
}

export function InstallCard({ capacitor }: InstallCardProps = {}) {
  const [checked, setChecked] = useState<null | 'ready' | 'missing'>(null);

  // After the hook, so the hook order is the same in the shell and in a tab even
  // though one of them renders nothing.
  if (capacitorBridge(capacitor) !== null) return null;

  const checkApk = async (): Promise<void> => {
    try {
      const response = await fetch(APK_PATH, { method: 'HEAD', credentials: 'same-origin' });
      // A redirect to the release asset answers 200 on HEAD; the honest 404
      // answers 404. Anything else is treated as missing rather than ready.
      setChecked(response.ok ? 'ready' : 'missing');
    } catch {
      setChecked('missing');
    }
  };

  return (
    <section className="card install" aria-label="Install on Android">
      <h3>Install on Android</h3>
      <p className="hint">
        Two ways onto a phone: the APK (camera plus on-device text reading) or the PWA (Add to Home Screen, no
        download). Both run the same scans and the same bins.
      </p>
      <div className="row">
        <a
          className="button primary"
          href={APK_PATH}
          onClick={() => void checkApk()}
        >
          Download the APK
        </a>
        <button type="button" className="ghost" onClick={() => void checkApk()}>
          Check availability
        </button>
      </div>
      {checked === 'missing' && (
        <p className="hint" role="status">
          No APK is published from this server yet. The build runs in CI on every push to main and attaches to
          releases tagged <code>android-v*</code>; ask the operator to publish one (<code>APK_FILE_PATH</code> on
          this server, or <code>APK_DOWNLOAD_URL</code>). Meanwhile the PWA works: open this page in Chrome,
          menu → Add to Home Screen.
        </p>
      )}
      {checked === 'ready' && (
        <p className="hint" role="status">
          The APK is published: tapping Download fetches it as a file. After copying it to the device, open it and
          allow “install unknown apps” for the file manager when asked.
        </p>
      )}
      {!checked && (
        <p className="hint">
          Sideloading: copy the APK to the device, open it, allow “install unknown apps” when asked. No Play review,
          no auto-update — the web bundle still updates itself over the air from your server.
        </p>
      )}
    </section>
  );
}
